import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Users, Leaf, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const props = [
  { icon: Calendar, text: "Pilotez tout le cycle de vie de vos événements" },
  { icon: Users, text: "Billetterie, invités et statistiques en temps réel" },
  { icon: Leaf, text: "Calculez le bilan carbone de chaque événement" },
];

export default function Login() {
  const { user, login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = mode === "login" ? await login(email, password) : await register(email, password, name, accessCode);
    setLoading(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Connexion réussie" : "Compte créé");
      navigate(mode === "login" ? "/dashboard" : "/profile");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 text-white overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-emerald-400 flex items-center justify-center">
            <Leaf className="h-6 w-6 text-white" />
          </div>
          <span className="font-display font-extrabold text-2xl tracking-tight">JustPlanIt</span>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight mb-4">
            Planifiez des événements professionnels éco-responsables
          </h1>
          <p className="text-indigo-200/80 text-lg mb-10 max-w-md">
            Le SaaS tout-en-un pour piloter vos événements de A à Z, en toute autonomie.
          </p>
          <div className="space-y-5">
            {props.map((p, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-indigo-900/60 border border-indigo-700/50 flex items-center justify-center">
                  <p.icon className="h-5 w-5 text-emerald-400" />
                </div>
                <p className="text-indigo-100">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-indigo-300/60">© 2026 JustPlanIt — Fait avec soin en France</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-emerald-400 flex items-center justify-center">
              <Leaf className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-extrabold text-xl">JustPlanIt</span>
          </div>
          <h2 className="font-display text-3xl font-bold mb-1">
            {mode === "login" ? "Bon retour 👋" : "Créer un compte"}
          </h2>
          <p className="text-muted-foreground mb-8">
            {mode === "login" ? "Connectez-vous à votre espace organisateur" : "Quelques secondes pour démarrer"}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Nom complet</Label>
                <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Marie Dupont" className="mt-1.5" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@entreprise.fr" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPwd ? "text" : "password"} required data-testid="password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pr-10" />
                <button type="button" tabIndex={-1} onClick={() => setShowPwd((v) => !v)} data-testid="toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700" aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {mode === "register" && (
              <div>
                <Label htmlFor="accessCode">Code d'accès</Label>
                <Input id="accessCode" required data-testid="access-code-input" value={accessCode} onChange={(e) => setAccessCode(e.target.value)} placeholder="XXXX-XXXX-XXXX" className="mt-1.5" />
                <p className="text-xs text-muted-foreground mt-1">Un code d'accès est requis pour créer un compte. Contactez l'organisateur pour l'obtenir.</p>
              </div>
            )}
            {error && <p className="text-sm text-rose-600" data-testid="auth-error">{error}</p>}
            {mode === "login" && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="remember-checkbox" />
                  Se souvenir de moi
                </label>
                <button type="button" className="text-sm text-primary hover:underline">Mot de passe oublié ?</button>
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="submit-auth-button" className="w-full h-11 text-base">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">ou</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Button variant="outline" onClick={loginWithGoogle} data-testid="google-login-button" className="w-full h-11">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 mr-2" />
            Continuer avec Google
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-8">
            {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
            <button
              type="button"
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setAccessCode(""); setName(""); }}
              className="text-primary font-semibold hover:underline"
            >
              {mode === "login" ? "Créer un compte" : "Se connecter"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
